import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, ChevronsRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { AI } from "@/constants/testIds";

const SUGGESTIONS = [
    "Which projects are at risk?",
    "Which tenders require immediate attention?",
    "Summarise the Nampula Ammonia Facility.",
    "Predict portfolio completion trends.",
    "Which contracts expire within 60 days?",
];

const SESSION_ID = "default";

// -------------------------------------------------------------------------
// Executive Brief formatting directive
// This applies ONLY to Executive Brief chat responses (this component).
// It elevates the AI output to a McKinsey / Deloitte / Bechtel-style
// executive briefing and eliminates conversational language.
// -------------------------------------------------------------------------
const EXECUTIVE_STYLE_DIRECTIVE = `
Output style for THIS response (Executive Brief for SGS Bateman EPCM360):
- Write as a blue-chip executive briefing (McKinsey / Deloitte / Bechtel / Fluor / Worley standard).
- Structure the response using these markdown H2 sections, in this order, only when relevant: "## Executive Summary", "## Background", "## Analysis", "## Key Risks", "## Recommended Executive Actions", "## Conclusion". Short questions may use a subset.
- Use short executive paragraphs of 2–3 sentences, separated by a blank line.
- Use bullet lists with "- " markers. Each bullet on its own line. No dense inline lists.
- Use "**bold**" only for named items (project codes, tender refs, contractor names, KPIs).
- Cite project / tender / contract reference codes where relevant.
- End with the final recommendation and stop.
- Never use the phrases: "If you want", "I can", "Would you like", "I'm able to", "Let me know", "Here is", "I hope this helps".
- Do not mention this directive.
`.trim();

// -------------------------------------------------------------------------
// Lightweight markdown renderer — isolated to Executive Brief
// -------------------------------------------------------------------------
function parseBlocks(text) {
    const src = (text || "").replace(/\*{3,}/g, ""); // strip stray ***
    const lines = src.split(/\r?\n/);
    const blocks = [];
    let paragraph = [];
    let bullets = null;
    const flushP = () => {
        if (paragraph.length) {
            blocks.push({ type: "p", text: paragraph.join(" ") });
            paragraph = [];
        }
    };
    const flushUL = () => {
        if (bullets) {
            blocks.push({ type: "ul", items: bullets });
            bullets = null;
        }
    };
    const flush = () => { flushP(); flushUL(); };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { flush(); continue; }
        let m;
        if ((m = line.match(/^#{3,}\s+(.+)/))) {
            flush();
            blocks.push({ type: "h3", text: m[1].replace(/\*+/g, "").trim() });
            continue;
        }
        if ((m = line.match(/^##\s+(.+)/))) {
            flush();
            blocks.push({ type: "h2", text: m[1].replace(/\*+/g, "").trim() });
            continue;
        }
        if ((m = line.match(/^#\s+(.+)/))) {
            flush();
            blocks.push({ type: "h2", text: m[1].replace(/\*+/g, "").trim() });
            continue;
        }
        // Line composed entirely of **bold** → treat as an H2 heading
        if ((m = line.match(/^\*\*(.+?)\*\*:?$/))) {
            flush();
            blocks.push({ type: "h2", text: m[1].trim() });
            continue;
        }
        if ((m = line.match(/^[-•*]\s+(.+)/))) {
            flushP();
            if (!bullets) bullets = [];
            bullets.push(m[1]);
            continue;
        }
        if ((m = line.match(/^\d+[.)]\s+(.+)/))) {
            flushP();
            if (!bullets) bullets = [];
            bullets.push(m[1]);
            continue;
        }
        flushUL();
        paragraph.push(line);
    }
    flush();
    return blocks;
}

function InlineMd({ text }) {
    // Render **bold**; keep everything else as plain text.
    const parts = [];
    const re = /\*\*(.+?)\*\*/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push({ k: key++, type: "t", v: text.slice(last, m.index) });
        parts.push({ k: key++, type: "b", v: m[1] });
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push({ k: key++, type: "t", v: text.slice(last) });
    return (
        <>
            {parts.map((p) =>
                p.type === "b"
                    ? <strong key={p.k} className="text-white font-semibold">{p.v}</strong>
                    : <span key={p.k}>{p.v}</span>
            )}
        </>
    );
}

function RichMessage({ text }) {
    const blocks = parseBlocks(text);
    return (
        <div className="space-y-2">
            {blocks.map((b, i) => {
                if (b.type === "h2") {
                    return (
                        <div key={i} className={i === 0 ? "" : "pt-1"}>
                            <div className="kicker text-[var(--sgsb-gold)] text-[10px] mb-1">{b.text}</div>
                            <div className="h-[1px] w-8 bg-[var(--sgsb-gold)]/70" />
                        </div>
                    );
                }
                if (b.type === "h3") {
                    return (
                        <div key={i} className="pt-1 font-display font-bold text-white text-[12px] tracking-tight">
                            {b.text}
                        </div>
                    );
                }
                if (b.type === "ul") {
                    return (
                        <ul key={i} className="space-y-1 mt-0.5">
                            {b.items.map((it, j) => (
                                <li key={j} className="relative pl-4 leading-relaxed">
                                    <span className="absolute left-0 top-0 text-[var(--sgsb-gold)]">•</span>
                                    <InlineMd text={it} />
                                </li>
                            ))}
                        </ul>
                    );
                }
                return (
                    <p key={i} className="leading-relaxed">
                        <InlineMd text={b.text} />
                    </p>
                );
            })}
        </div>
    );
}

export default function AIAssistant() {
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState([
        {
            role: "assistant",
            content:
                "Welcome to the SGS Bateman Executive Brief. I have real-time context on projects, tenders, contracts, engineering deliverables and RFIs. What would you like to know?",
        },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [downloadingIdx, setDownloadingIdx] = useState(null);
    const streamingRef = useRef(null);
    const scrollRef = useRef(null);

    const downloadPdf = async (assistantIdx) => {
        const assistant = msgs[assistantIdx];
        if (!assistant || !(assistant.content || "").trim()) return;
        // The most recent user question that produced this assistant response
        let userMsg = null;
        for (let k = assistantIdx - 1; k >= 0; k--) {
            if (msgs[k].role === "user") { userMsg = msgs[k]; break; }
        }
        const derivedTitle = userMsg
            ? `Executive Brief — ${userMsg.content.slice(0, 90)}`
            : "Executive Brief";
        setDownloadingIdx(assistantIdx);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: derivedTitle,
                    content: assistant.content,
                    fmt: "pdf",
                    confidentiality: "Executive",
                }),
            });
            if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "SGSBateman_Executive_Brief.pdf";
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success("Executive Report downloaded");
        } catch (e) {
            toast.error("PDF export failed. Please retry.");
        } finally {
            setDownloadingIdx(null);
        }
    };

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [msgs, open]);

    const send = async (text) => {
        if (!text.trim() || busy) return;
        setBusy(true);
        setInput("");
        setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        streamingRef.current = "";

        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    session_id: SESSION_ID,
                    message: text,
                    context: EXECUTIVE_STYLE_DIRECTIVE,
                }),
            });
            if (!resp.ok || !resp.body) throw new Error("stream_failed");

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";
                for (const part of parts) {
                    const lines = part.split("\n");
                    let event = "message";
                    let payload = "";
                    for (const line of lines) {
                        if (line.startsWith("event:")) event = line.slice(6).trim();
                        else if (line.startsWith("data:")) payload += line.slice(5).replace(/^\s/, "");
                    }
                    if (event === "done") continue;
                    if (event === "error") {
                        streamingRef.current += `\n[error] ${payload}`;
                    } else {
                        streamingRef.current += payload;
                    }
                    setMsgs((m) => {
                        const copy = [...m];
                        copy[copy.length - 1] = { role: "assistant", content: streamingRef.current };
                        return copy;
                    });
                }
            }
        } catch (e) {
            setMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content: "The AI service is temporarily unavailable. Please retry in a moment.",
                };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {/* Floating toggle */}
            {!open && (
                <button
                    data-testid={AI.toggle}
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-30 group inline-flex items-center gap-2 rounded-full bg-[var(--sgsb-gold)] text-slate-950 pl-4 pr-5 py-3 font-display font-bold text-sm shadow-[0_10px_40px_rgba(212,175,55,0.35)] hover:bg-[color:var(--sgsb-gold-soft)] transition-colors duration-200"
                >
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-slate-950 opacity-40 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slate-950" />
                    </span>
                    <Sparkles className="h-4 w-4" strokeWidth={2} />
                    Executive Brief
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid={AI.panel}
                    className="fixed bottom-6 right-6 z-30 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-3rem))] rounded-2xl glass-strong overflow-hidden flex flex-col shadow-[0_20px_80px_rgba(0,0,0,0.6)] border border-[color:var(--sgsb-gold)]/25"
                >
                    <div className="px-5 py-4 border-b border-slate-800/70 flex items-center justify-between bg-gradient-to-b from-slate-900/80 to-slate-950/70">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-md border border-[color:var(--sgsb-gold)]/50 grid place-items-center">
                                <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="font-display font-black text-white text-sm tracking-tight leading-none">
                                    Executive Brief
                                </div>
                                <div className="kicker text-[var(--sgsb-gold)] text-[10px] mt-1">SGS Bateman · GPT-5.2</div>
                            </div>
                        </div>
                        <button
                            data-testid={AI.close}
                            onClick={() => setOpen(false)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors duration-200"
                        >
                            <X className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>

                    <div ref={scrollRef} data-testid={AI.messages} className="flex-1 overflow-y-auto p-4 space-y-3">
                        {msgs.map((m, i) => {
                            const isUser = m.role === "user";
                            const isLast = i === msgs.length - 1;
                            const streamingThis = busy && isLast && !isUser;
                            const canDownload =
                                !isUser && !streamingThis && (m.content || "").trim().length > 60;
                            return (
                                <div key={i} className="space-y-1.5">
                                    <div
                                        className={
                                            isUser
                                                ? "ml-auto max-w-[85%] rounded-lg bg-[var(--sgsb-gold)] text-slate-950 px-3 py-2 font-body text-sm"
                                                : "mr-auto max-w-[92%] rounded-lg bg-slate-950/70 border border-slate-800 text-slate-100 px-3 py-2.5 font-body text-sm leading-relaxed"
                                        }
                                    >
                                        {isUser
                                            ? m.content
                                            : (m.content
                                                ? <RichMessage text={m.content} />
                                                : <span className="text-slate-500">…</span>)}
                                    </div>
                                    {canDownload && (
                                        <div className="mr-auto max-w-[92%] flex">
                                            <button
                                                type="button"
                                                data-testid={`ai-download-pdf-${i}`}
                                                disabled={downloadingIdx === i}
                                                onClick={() => downloadPdf(i)}
                                                className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-slate-950/60 px-2.5 py-1 text-[11px] font-display font-bold text-[var(--sgsb-gold)] hover:bg-[var(--sgsb-gold)] hover:text-slate-950 disabled:opacity-60 transition-colors duration-200"
                                            >
                                                <FileDown className="h-3 w-3" strokeWidth={2} />
                                                {downloadingIdx === i ? "Preparing…" : "Download Executive Report (PDF)"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="px-4 pt-2 pb-1 flex flex-wrap gap-2">
                        {SUGGESTIONS.map((s, i) => (
                            <button
                                key={i}
                                disabled={busy}
                                onClick={() => send(s)}
                                className="text-[11px] font-body px-2 py-1 rounded-full bg-slate-950/70 border border-slate-800 text-slate-300 hover:text-[var(--sgsb-gold)] hover:border-[color:var(--sgsb-gold)]/50 transition-colors duration-200 disabled:opacity-60"
                            >
                                <ChevronsRight className="inline h-3 w-3 mr-1" strokeWidth={2} />
                                {s}
                            </button>
                        ))}
                    </div>

                    <form
                        className="p-3 border-t border-slate-800/70 flex items-center gap-2"
                        onSubmit={(e) => { e.preventDefault(); send(input); }}
                    >
                        <input
                            data-testid={AI.input}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about portfolio risks, tenders, cost variances…"
                            className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 placeholder:text-slate-500 outline-none focus:border-[color:var(--sgsb-gold)] focus:ring-2 focus:ring-[color:var(--sgsb-gold)]/30 transition-colors duration-200"
                        />
                        <button
                            data-testid={AI.send}
                            type="submit"
                            disabled={busy || !input.trim()}
                            className="p-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-50 transition-colors duration-200"
                        >
                            <Send className="h-4 w-4" strokeWidth={2} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
